/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Grid, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import FxCard from '../../../Container/FxCard';
import FxCardBody from '../../../Container/FxCardBody';
import { FxButton } from '../../../Action/FxButton';
import { ReactComponent as WarningIcon } from '../../../../assets/svg/signup/warning-triangle.svg';

/**
 * Author: Shivaraj PP
 * Description: This component handles unauthorized access messages.
 */
interface SomethingWentWrongProps {
    id?: string;
    title?: string;
}

interface ErrorMessage {
    errorCode: string;
    titleMessage: string;
    subMessage: string;
}

const errorMessages: ErrorMessage[] = [
    {
        errorCode: '400',
        titleMessage: 'Invalid Request.',
        subMessage: "There's an issue with the information you provided. Please check your input and try again.",
    },
    {
        errorCode: '401',
        titleMessage: 'Please Log In to Continue.',
        subMessage: "Your session has expired or you're not logged in. Please log in to continue.",
    },
    {
        errorCode: '403',
        titleMessage: 'Access Denied.',
        subMessage: "You don't have the necessary permissions to view this content or perform this action.",
    },
    {
        errorCode: '500',
        titleMessage: 'Oops, Something Went Wrong',
        subMessage: "We're experiencing a technical glitch. Please try again in a few moments, and if the issue persists, contact support.",
    },
    {
        errorCode: '502',
        titleMessage: 'Oops, Something Went Wrong',
        subMessage: "We're having a temporary issue connecting to our services. Please refresh the page or try again shortly.",
    },
    {
        errorCode: '503',
        titleMessage: 'Oops, Something Went Wrong',
        subMessage: "Our services are temporarily unavailable while we're performing maintenance. Please try again soon!.",
    },
    {
        errorCode: '504',
        titleMessage: 'Oops, Connection Timed Out.',
        subMessage: 'The request took too long to complete. Please try again',
    },
];

export const SomethingWentWrong: React.FC<SomethingWentWrongProps> = React.memo((props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { errorCode } = useParams<{ errorCode: string }>();

    /**
     * Finds the matching error message object based on the current error code.
     *
     * - `errorMessages` is an array of predefined error entries.
     * - `find()` returns the first match where `errorCode` matches the current route param.
     * - If no match is found, it falls back to a default object with a generic message.
     *
     * This ensures the UI always has a `titleMessage` and `subMessage` to show,
     * even for unknown or unexpected error codes.
     */
    const errorMessage =
        errorMessages.find((e) => e.errorCode === errorCode) || {
            errorCode: '',
            titleMessage: 'An unexpected error occurred.',
            subMessage: '',
        };

    const retryCodes = ['500', '502', '503', '504'];
    const isRetry = retryCodes.includes(errorCode);

    /**
     * useEffect: Component Initialization
     */
    useEffect(() => {
        dispatch({ type: 'DATA_COMPONENT_RESET', payload: { id: props.id } });
    }, []);

    /**
     * Handles the action button click.
     *
     * - If the error code represents a retryable server error (like 500, 502, etc.),
     *   it redirects the user to `/postauth` to attempt recovery or retry login.
     *
     * - For all other errors (e.g., 401, 403), it assumes the user must log in again
     *   and redirects to the `/logout` route, which will typically clear the session
     *   and navigate to the login page.
     */
    const buttonAction = () => {
        history.push(isRetry ? '/postauth' : '/logout');
    };

    return (
        <Grid id="fx-404-grid" item xs={12} className="fx-404 fx-404-reduced-height">
            <FxCard id="fx-404-card">
                <FxCardBody id="fx-404-card-body">
                    <Grid item container justifyContent="center" xs={12} className="fx-margin-bottom-2">
                        <WarningIcon />
                    </Grid>

                    <Grid item container justifyContent="center" xs={12}>
                        <Typography variant="h5" id="fx-404-fourth-message">
                            {errorMessage.titleMessage}
                        </Typography>
                    </Grid>

                    {/* Sub-Message Section */}
                    <Grid item container direction="column" alignItems="center" xs={12} className="fx-margin-top-1">
                        <Typography variant="h6">{errorMessage.subMessage}</Typography>
                    </Grid>

                    <Grid item container justifyContent="center" xs={12}>
                        <FxButton
                            className="fx-button fx-button-theme fx-margin-top-2"
                            variant="contained"
                            id={`${props.id ?? 'unauth'}-button`}
                            onClick={buttonAction}
                        >
                            {isRetry ? 'Try Again' : 'Continue to Login'}
                        </FxButton>
                    </Grid>
                </FxCardBody>
            </FxCard>
        </Grid>
    );
});
